import { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { LocationPromotionRow, PerformanceResponse } from '../types';
import {
  CategoryBadge,
  CompositionDonut,
  EmptyPeriodState,
  KpiStrip,
  LocationChips,
  VerdictBadge,
  engineTableCss,
  isEmptyEnginePeriod,
  money,
  pct,
  roi
} from './shared';

const PerformanceTab = ({ data }: { data: PerformanceResponse }) => {
  const currency = data.currency_code || '';
  const [drillLocationId, setDrillLocationId] = useState<number>(
    data.locations[0]?.location_id || 0
  );

  const drillPromos = useMemo<LocationPromotionRow[]>(() => {
    const id = drillLocationId || data.locations[0]?.location_id;
    if (!id) return [];
    return (data.location_promotions || [])
      .filter((row) => row.location_id === id)
      .sort((a, b) => a.margin_impact - b.margin_impact);
  }, [data.location_promotions, data.locations, drillLocationId]);

  if (isEmptyEnginePeriod(data.kpis)) {
    return <EmptyPeriodState />;
  }

  return (
    <Wrap>
      <Hero>
        <HeroLeft>
          <Lbl>Promo penetration</Lbl>
          <Big>{pct(data.kpis.penetration_pct)}</Big>
          <Cap>
            of total revenue ran through a promotion.{' '}
            <b>{pct(data.composition.full_price_pct)}</b> was without a promotion.
          </Cap>
          <Readout>
            Discount spend <b>{money(data.kpis.promo_discount, currency)}</b> with ROI{' '}
            <b>{roi(data.kpis.promo_roi)}</b>. Promo-ticket gross margin{' '}
            <b>{money(data.kpis.margin_impact, currency)}</b>.
          </Readout>
        </HeroLeft>
        <div>
          <Lbl>Revenue mix — {money(data.kpis.net_revenue, currency)}</Lbl>
          <CompositionDonut composition={data.composition} currency={currency} />
        </div>
      </Hero>

      <KpiStrip kpis={data.kpis} currency={currency} />
      <Formula>{data.roi_formula}</Formula>

      <SectionTitle>Multi-unit breakdown</SectionTitle>
      <Panel $scroll>
        <Table>
          <thead>
            <tr>
              <th>Outlet</th>
              <th className="num">Revenue</th>
              <th className="num">Promo penetration</th>
              <th className="num">Discount</th>
              <th className="num">Gross margin</th>
              <th className="num">Margin impact</th>
              <th className="num">ROI</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.locations.length === 0 ? (
              <tr>
                <td colSpan={8}>No location data.</td>
              </tr>
            ) : (
              data.locations.map((l) => (
                <tr
                  key={l.location_id}
                  onClick={() => setDrillLocationId(l.location_id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{l.location_name}</td>
                  <td className="num">{money(l.net_revenue, currency)}</td>
                  <td className="num">{pct(l.penetration_pct)}</td>
                  <td className="num">{money(l.promo_discount, currency)}</td>
                  <td className="num">{money(l.gross_margin, currency)}</td>
                  <td className={`num ${l.margin_impact < 0 ? 'neg' : 'pos'}`}>
                    {money(l.margin_impact, currency)}
                  </td>
                  <td className="num">{roi(l.promo_roi)}</td>
                  <td>
                    <VerdictBadge verdict={l.verdict} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Panel>

      <SectionTitle>Location → promotion</SectionTitle>
      <Panel>
        <DrillBar>
          <label htmlFor="pe-perf-location">Location</label>
          <Select
            id="pe-perf-location"
            value={drillLocationId || ''}
            onChange={(e) => setDrillLocationId(Number(e.target.value))}
          >
            {data.locations.map((l) => (
              <option key={l.location_id} value={l.location_id}>
                {l.location_name}
              </option>
            ))}
          </Select>
        </DrillBar>
        {drillPromos.length === 0 ? (
          <Empty>No promotions applied at this location in the period.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Promotion</th>
                <th className="num">Orders</th>
                <th className="num">Promo rev</th>
                <th className="num">Discount</th>
                <th className="num">Gross margin</th>
                <th className="num">ROI</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {drillPromos.map((p) => (
                <tr key={`${p.promotion_id}-${p.location_id}`}>
                  <td>{p.promotion_name}</td>
                  <td className="num">{p.orders.toLocaleString()}</td>
                  <td className="num">{money(p.promo_revenue, currency)}</td>
                  <td className="num">{money(p.promo_discount, currency)}</td>
                  <td className={`num ${p.margin_impact < 0 ? 'neg' : 'pos'}`}>
                    {money(p.margin_impact, currency)}
                  </td>
                  <td className="num">{roi(p.promo_roi)}</td>
                  <td>
                    <VerdictBadge verdict={p.verdict} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      <SectionTitle>Promotion by channel</SectionTitle>
      <Panel $scroll>
        <Table>
          <thead>
            <tr>
              <th>Channel</th>
              <th className="num">Revenue</th>
              <th className="num">Mix</th>
              <th className="num">Promo rev</th>
              <th className="num">Penetration</th>
              <th className="num">Discount</th>
              <th className="num">Gross margin</th>
              <th className="num">Margin impact</th>
              <th className="num">ROI</th>
              <th className="num">Orders</th>
            </tr>
          </thead>
          <tbody>
            {(data.channels || []).length === 0 ? (
              <tr>
                <td colSpan={10}>No channel data.</td>
              </tr>
            ) : (
              data.channels.map((c) => (
                <tr key={c.order_channel_id}>
                  <td>{c.order_channel}</td>
                  <td className="num">{money(c.net_revenue, currency)}</td>
                  <td className="num">{pct(c.revenue_contribution_pct)}</td>
                  <td className="num">{money(c.promo_revenue, currency)}</td>
                  <td className="num">{pct(c.penetration_pct)}</td>
                  <td className="num">{money(c.promo_discount, currency)}</td>
                  <td className="num">{money(c.gross_margin, currency)}</td>
                  <td className={`num ${c.margin_impact < 0 ? 'neg' : 'pos'}`}>
                    {money(c.margin_impact, currency)}
                  </td>
                  <td className="num">{roi(c.promo_roi)}</td>
                  <td className="num">{c.orders.toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Panel>

      <SectionTitle>Promotion-level performance</SectionTitle>
      <Panel $scroll>
        <Table>
          <thead>
            <tr>
              <th>Promotion</th>
              <th>Status</th>
              <th className="num">Orders</th>
              <th className="locs">Locations</th>
              <th className="num">Discount</th>
              <th className="num">Promo rev</th>
              <th className="num">Gross margin</th>
              <th className="num">ROI</th>
              <th>Category</th>
            </tr>
          </thead>
          <tbody>
            {data.promotions.length === 0 ? (
              <tr>
                <td colSpan={9}>No promotions in this period.</td>
              </tr>
            ) : (
              data.promotions.map((p) => (
                <tr key={p.promotion_id}>
                  <td>{p.promotion_name}</td>
                  <td>{p.status}</td>
                  <td className="num">{p.orders.toLocaleString()}</td>
                  <td className="locs">
                    <LocationChips names={p.location_names} count={p.location_count} />
                  </td>
                  <td className="num">{money(p.promo_discount, currency)}</td>
                  <td className="num">{money(p.promo_revenue, currency)}</td>
                  <td className={`num ${p.margin_impact < 0 ? 'neg' : 'pos'}`}>
                    {money(p.margin_impact, currency)}
                  </td>
                  <td className="num">{roi(p.promo_roi)}</td>
                  <td>
                    <CategoryBadge categoryKey={p.category_key} label={p.category_label} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Panel>
    </Wrap>
  );
};

export default PerformanceTab;

const Wrap = styled.div``;
const Hero = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 24px;
  background: ${({ theme }) => theme.widget_group?.background || '#f4f5f7'};
  border: 1px solid ${({ theme }) => theme.border_color};
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  border-radius: 14px;
  padding: 22px 24px;
  margin-bottom: 16px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
const HeroLeft = styled.div``;
const Lbl = styled.div`
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 700;
  color: ${({ theme }) => theme.text_muted || theme.text};
  margin-bottom: 8px;
`;
const Big = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 700;
  font-size: 3.15rem;
  line-height: 1;
  background: linear-gradient(100deg, #ff5a2d, #f0347f);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;
const Cap = styled.div`
  margin-top: 10px;
  font-size: 1rem;
  color: ${({ theme }) => theme.text_secondary || theme.text};
  max-width: 36ch;
`;
const Readout = styled.div`
  margin-top: 14px;
  font-size: 0.92rem;
  color: ${({ theme }) => theme.text};
  background: rgba(255, 90, 45, 0.06);
  border: 1px solid rgba(255, 90, 45, 0.2);
  border-left: 3px solid #ff5a2d;
  border-radius: 10px;
  padding: 10px 12px;
`;
const Formula = styled.p`
  margin: 0 0 8px;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.text_muted || theme.text_secondary || theme.text};
`;
const SectionTitle = styled.h2`
  font-size: 0.82rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text_muted || theme.text};
  margin: 22px 2px 12px;
  font-weight: 700;
`;
const Panel = styled.div<{ $scroll?: boolean }>`
  background: ${({ theme }) => theme.widget_group?.background || '#f4f5f7'};
  border: 1px solid ${({ theme }) => theme.border_color};
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  border-radius: 14px;
  padding: 16px 18px;
  overflow: ${({ $scroll }) => ($scroll ? 'auto' : 'visible')};
`;
const Table = styled.table`
  ${engineTableCss}
  th:nth-child(2),
  td:nth-child(2) {
    text-align: left;
    font-family: inherit;
  }
  th.locs,
  td.locs {
    padding-left: 24px;
  }
`;
const DrillBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  label {
    font-size: 0.82rem;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
  }
`;
const Select = styled.select`
  min-width: 220px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border_color};
  background: ${({ theme }) => theme.widget.background};
  color: ${({ theme }) => theme.text};
  font-size: 0.9rem;
`;
const Empty = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.text_muted || theme.text};
`;
